import {
  AvailabilityApi,
  DocumentApiFactory,
  DocumentSigningApiFactory,
  ElementTreeApi,
  EventsApiFactory,
  ImagesApiFactory,
  ObjectApiFactory,
  RuleEvaluatorApi,
  TokenApiFactory,
  UserApi,
  UserApiApiFactory,
  RequirementValidationResultsApiFactory,
} from "@radianse/services";
import { MemberApiFactory } from "@radianse/elements-api";
import axios from "axios";
import { TOKEN_NAME, SESSION, DEVICE_ID } from "../context/ApiAuthContext";

export const _basePath = process.env.BASE_PATH || "";

function parseJwt(token) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export function refreshAccessToken() {
  const deviceId = localStorage.getItem(DEVICE_ID);
  const session = localStorage.getItem(SESSION);
  const token = localStorage.getItem(TOKEN_NAME);
  // check if token expired
  const decodedToken = parseJwt(token);
  if (!token || decodedToken.exp < new Date().getTime() / 1000) {
    return TokenApiClient.getServiceClientToken("en-US,en", {
      clientId: deviceId,
      clientSecret: session,
    }).then((res) => {
      localStorage.setItem(TOKEN_NAME, res?.data?.token);
      return res?.data?.token;
    });
  }
  return token;
}

// Axios Instance with Auth token
const createApiInstance = (basePath) => {
  const instance = axios.create({
    baseURL: `${window["_env"].IDENTITY_API}${basePath}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(TOKEN_NAME)}`,
    },
  });

  // Request interceptor for API calls
  instance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(TOKEN_NAME);
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        };
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  // Response interceptor for API calls
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async function (error) {
      const originalRequest = error.config;
      if (
        (error?.response?.status === 401 || error?.response?.status === 403) &&
        !originalRequest?._retry
      ) {
        originalRequest._retry = true;
        const access_token = await refreshAccessToken();
        instance.defaults.headers.common["Authorization"] =
          "Bearer " + access_token;
        return instance(originalRequest);
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

let tokenRefreshTimeout;

const scheduleTokenRefresh = () => {
  // Clear any existing timeout
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
  }

  const token = localStorage.getItem(TOKEN_NAME);
  if (!token) return;

  try {
    const decodedToken = parseJwt(token);
    const now = Math.floor(Date.now() / 1000);

    // Refresh 5 minutes before expiration
    const timeUntilExpiry = decodedToken.exp - now;
    const refreshTime = Math.max(0, timeUntilExpiry - 300) * 1000; // 5 mins before expiry

    if (refreshTime > 0) {
      tokenRefreshTimeout = setTimeout(async () => {
        try {
          await refreshAccessToken();
          scheduleTokenRefresh(); // Schedule next refresh
        } catch (error) {
          console.error("Auto-refresh failed:", error);
        }
      }, refreshTime);
    }
  } catch (error) {
    console.error("Error scheduling token refresh:", error);
  }
};

// Initial setup for auto-refresh
if (typeof window !== "undefined") {
  // Schedule initial refresh when page loads
  scheduleTokenRefresh();

  // Also schedule refresh on token change events if needed
  const originalSetItem = localStorage.setItem;
  localStorage.setItem = function (key, value) {
    if (key === TOKEN_NAME) {
      originalSetItem.apply(this, arguments);
      scheduleTokenRefresh(); // Schedule new refresh when token changes
    } else {
      originalSetItem.apply(this, arguments);
    }
  };
}

export const identity_instance = createApiInstance("/app/identity");
export const identity_instance_elements = createApiInstance("/app/elements");
export const identity_instance_documents = createApiInstance("/app/documents");
export const identity_instance_rules = createApiInstance("/app/rules");
export const identity_instance_users = createApiInstance("/app/users");
export const identity_instance_availability =
  createApiInstance("/app/availability");

export const baseOptions = {
  timeout: 60000,
};
export const TokenApiClient = TokenApiFactory(
  {},
  `${window["_env"].IDENTITY_API}/identity`,
  identity_instance
);

export const objectApiClient = ObjectApiFactory(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/elements`,
  identity_instance_elements
);

export const UserApiApiClient = UserApiApiFactory(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/users`,
  identity_instance_users
);
export const UserApiClient = new UserApi(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/users`,
  identity_instance_users
);

export const availApiClient = new AvailabilityApi(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/availability`,
  identity_instance_availability
);

export const rulesApiClient = new RuleEvaluatorApi(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/rules`,
  identity_instance_rules
);

export const RequirementValidationResultsApiClient =
  new RequirementValidationResultsApiFactory(
    { baseOptions },
    `${window["_env"].IDENTITY_API}/app/rules`,
    identity_instance_rules
  );

export const elementTreeApiClient = new ElementTreeApi(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/elements`,
  identity_instance_elements
);

export const eventsApiClient = EventsApiFactory(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/elements`,
  identity_instance_elements
);
export const imagesApiClient = ImagesApiFactory(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/elements`,
  identity_instance_elements
);

export const DocumentApiClient = DocumentApiFactory(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/documents`,
  identity_instance_documents
);

export const DocumentSigningApiClient = DocumentSigningApiFactory(
  { baseOptions },
  `${window["_env"].IDENTITY_API}/app/documents`,
  identity_instance_documents
);

export const MemberApiClient = MemberApiFactory(
  {
    basePath: window["_env"].IDENTITY_API + "/app/elements",
    baseOptions: {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_NAME)}`,
      },
    },
  },
  undefined,
  identity_instance_elements
);
